import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useContext,
} from "react";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarExportContainer,
  GridCsvExportMenuItem,
  GridPrintExportMenuItem,
  GridToolbarColumnsButton,
  GridLogicOperator,
} from "@mui/x-data-grid";
import axios from "axios";
import { Box } from "@mui/system";
import { ListItemButton, TextField, Typography } from "@mui/material";
import { ListItemText } from "@mui/material";
import "./SimpleTable.css";
import { exportToExcel } from "./tools";
import DI from "../../../utility/DI";
import { FetchContext } from "../../SideBar";
import { environment } from "../../../../Env_data/environment";
import { AppContext } from "../../../../App";
const column_setting = environment.APP_NAME + "_columnVisibilityModel";
const SimpleTable = ({
  mode = "server",
  apiEndpoint,
  columns,
  docTitle,
  session,
  di: {logout, error },
  ...props
}) => {
  const debounceRef = React.useRef(null);
  const [data, setData] = useState([]);
  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 10,
  });
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [isSelectAll, setIsSelectAll] = useState(false);

  // Initialize columnVisibilityModel from localStorage
  const initialColumnVisibilityModel = JSON.parse(
    localStorage.getItem(column_setting)
  ) || {
    brand: false,
    id: false,
    weight: false,
    __check__: false,
  };
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(
    initialColumnVisibilityModel
  );
  const [searchQuery, setSearchQuery] = useState("");
  const { page, pageSize } = paginationModel;
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(mode === "server");
  const [sortModel, setSortModel] = useState([]);
  const [filterModel, setFilterModel] = useState({
    items: [],
    logicOperator: GridLogicOperator.Or,
  });
  const [filterdTotal, setFilterdTotal] = useState(0);
  const { refresh } = useContext(FetchContext);
  const contextData = useContext(AppContext);
  React.useEffect(() => {
    fetchData();
  }, [refresh]);

  useEffect(() => {
    localStorage.setItem(column_setting, JSON.stringify(columnVisibilityModel));
  }, [columnVisibilityModel]);
  /**
   * api for get data do filter do search and pagination
   */
  const fetchData = useCallback(
    async (all = false) => {
      setLoading(true);
      try {
        let params = {
          query: searchQuery,
          sort: sortModel
            .map(({ field, sort }) => `${field}:${sort}`)
            .join(","),
          filters: filterModel.items
            .map(
              ({ field: columnField, operator: operatorValue, value }) =>
                `${columnField}:${operatorValue}:${value ?? ""}`
            )
            .join(","),
          home_id: props?.currentHome,
          user_id: session?.id,
        };
        if (!all)
          params = { ...params, skip: page * pageSize, limit: pageSize };
        if (mode === "server" && rowSelectionModel.length)
          params = {
            ...params,
            selected_ids: isSelectAll ? "all" : rowSelectionModel,
          };
        if (mode === "server" && sortModel.length)
          params = { ...params, sort: sortModel };
        params["auth_id"] = contextData?.state?.session?.id ?? "";
        params["bearer"] = contextData?.state?.session?.bearer ?? "";
        const response = await axios.get(apiEndpoint, {
          params,
        });
        if (response?.error_code === "re-login") {
          setData([]);
          setRowCount(0)
          setFilterdTotal(0);
          setRowSelectionModel([]);
          await logout();
          await error(data?.message ?? "Session expired. Please login again.");
        }else if (response.data) {
          const arrData = response.data.data;
          setData(arrData);
          setRowCount(response.data.total);
          setFilterdTotal(response.data.filtered_total);
          if (mode === "server" && (isSelectAll || props?.defaultSelection)) {
            setRowSelectionModel((prev) => {
              const remainingId = arrData
                ?.filter((row) => {
                  if (!row?.id) {
                    console.error("Missing 'id' in row:", row);
                    return false; // Skip rows without an 'id'
                  }
                  const availableIndex = prev.find((id) => row?.id === id);
                  if (props?.isRowSelectable) {
                    if (isSelectAll)
                      return (
                        availableIndex === undefined &&
                        props?.isRowSelectable({ row, id: row.id, columns: [] })
                      );
                    else if (props?.defaultSelection)
                      return (
                        availableIndex === undefined &&
                        props.defaultSelection({ row, id: row.id, columns: [] })
                      );
                  }
                  return availableIndex === undefined;
                })?.map((el) => el?.id)??[];
              return [...prev, ...remainingId];
            });
          }
        }
      } catch (error) {
        console.error("Error fetching data", error);
      } finally {
        setLoading(false);
      }
    },
    [
      searchQuery,
      filterModel,
      sortModel,
      paginationModel,
      isSelectAll,
      rowSelectionModel,
    ]
  );

  /**
   * single call for client side
   */
  useEffect(() => {
    if (mode === "client") {
      fetchData(true);
    }
  }, []);

  /**
   * multiple call on change of inputs wjhile server side
   */
  useEffect(() => {
    if (mode === "server") {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        fetchData();
      }, 300);
    }
  }, [page, pageSize, sortModel, filterModel, mode, searchQuery]);

  const handleSortChange = (newSortModel) => {
    setSortModel(newSortModel);
    if (mode === "server") {
      setPaginationModel({
        ...paginationModel,
        page: 0,
      }); // Reset to first page whenever sorting changes
    }
  };

  /**
   * export excel from json data
   * in future we can placed apii call here to fetch data from api then convert into excel to bypass pagination
   */
  const exportExcel = () => {
    let arr = [];
    data.forEach((row) => {
      let obj = {};
      columns.forEach((col) => {
        obj[col.headerName] = row[col.field];
      });
      arr.push(obj);
    });
    exportToExcel(arr, docTitle);
  };

  /**
   * Export button in toolbar
   * @param param
   * @returns
   */
  const GridToolbarExport = ({ csvOptions, printOptions, ...other }) => (
    <GridToolbarExportContainer {...other}>
      <GridCsvExportMenuItem options={csvOptions} />
      <GridPrintExportMenuItem
        options={{
          hideToolbar: true,
        }}
      />
      <ListItemButton sx={{ py: "0px" }} onClick={exportExcel}>
        <ListItemText primary="Download as EXCEL" />
      </ListItemButton>
    </GridToolbarExportContainer>
  );

  const CustomToolbar = useCallback(
    (CustomToolbar, data) => {
      return (
        <div
          style={{ display: "flex", flexDirection: "column", width: "100%" }}
        >
          <div>
            {" "}
            <GridToolbarContainer>
              <GridToolbarColumnsButton />
              {/* <GridToolbarFilterButton
                />
                <GridToolbarDensitySelector
                  slotProps={{ tooltip: { title: "Change density" } }}
                /> */}

              <GridToolbarExport />
              <Box sx={{ flexGrow: 1 }} />
              {/* <GridToolbarQuickFilter
                  quickFilterParser={(searchInput) =>
                    searchInput.split(",").map((value) => value.trim())
                  }
                  quickFilterFormatter={(quickFilterValues) =>
                    quickFilterValues.join(", ")
                  }
                  debounceMs={200} // time before applying the new quick filter value
                /> */}
            </GridToolbarContainer>
          </div>
        </div>
      );
    },
    [data, filterModel]
  );
  const handleSearch = (event) => {
    const query = event.target.value;
    setSearchQuery(query);
  };
  const rowSelectionHandler = (selectedRows) => {
    let requiredSize = paginationModel.pageSize;
    if (props?.isRowSelectable) {
      requiredSize = data.filter(
        (row) =>
          props?.isRowSelectable &&
          props?.isRowSelectable({ row, id: row.id, columns: [] })
      ).length;
    }
    if (
      isSelectAll &&
      selectedRows.length === rowSelectionModel.length - requiredSize
    ) {
      setIsSelectAll(false);
      setRowSelectionModel([]);
    } else {
      const isAllSelection =
        data.filter((data) => selectedRows.find((id) => id === data?.id))
          .length === requiredSize;
      setIsSelectAll(isAllSelection);
      setRowSelectionModel(selectedRows);
    }
  };
  /**
   * column search input handler logic
   */
  const columnFilterHandler = useCallback((e, params) => {
    e.stopPropagation();
    if (mode === "server") {
      setPaginationModel({
        ...paginationModel,
        page: 0,
      });
    }
    setFilterModel((prev) => {
      const index = prev.items.findIndex(
        (el) => el.field === params.colDef.field
      );
      if (index !== -1) {
        if (e.target.value == "") {
          prev.items.splice(index, 1);
        } else prev.items[index].value = e.target.value;
      } else {
        prev.items.push({
          field: params.field,
          operator: params.colDef.type === "number" ? "=" : "contains",
          value: e.target.value,
        });
      }
      return { ...prev };
    });
  }, []);

  const columnsWithFilters = useMemo(() => {
    const headers = columns;
    return headers.map((column) => {
      if (column?.headerName && !column.hasOwnProperty("width")) {
        column.width = column.headerName?.split("").length * 10;
      }
      return {
        ...column,
        renderHeader: (params) => {
          return (
            <Box
              display="flex"
              flexDirection="column"
              // style={{ minHeight: "100px", paddingTop: "25px" }}
            >
              <Typography variant="body2" fontWeight={600} gutterBottom>
                {params.colDef.headerName}
              </Typography>
              {column?.filterable !== false && (
                <div
                  className="datagrid_input-column"
                  onClick={(e) => e.stopPropagation()}
                >
                  <TextField
                    placeholder="Search"
                    style={{
                      padding: "2px !important",
                    }}
                    variant="outlined"
                    size="small"
                    value={
                      filterModel.items.find(
                        (el) => el.field === params.colDef.field
                      )?.value ?? ""
                    }
                    onChange={(e) => columnFilterHandler(e, params)}
                  />
                </div>
              )}
            </Box>
          );
        },
      };
    });
  }, [filterModel]);

  const tableProps = useMemo(
    () => {
      const obj = {
        pagination: true,
        density: "compact",
        columns: columnsWithFilters,
        disableColumnFilter: true,
        autoHeight: true,
        disableColumnMenu: true,
        columnHeaderHeight: 110,
        disableRowSelectionOnClick: true,
        checkboxSelection: true,
        columnVisibilityModel: columnVisibilityModel,
        onColumnVisibilityModelChange: setColumnVisibilityModel,
        slotProps: {
          toolbar: {
            showQuickFilter: true,
            printOptions: {
              hideFooter: true,
              hideToolbar: true,
            },
          },
        },
        slots: {
          toolbar: CustomToolbar,
        },
        initialState: {
          pagination: { paginationModel: { pageSize: 20 } },
        },
      };
      if (props?.onRowClick) obj.onRowClick = props.onRowClick;
      if (props?.onCellClick) obj.onCellClick = props.onCellClick;
      if (props?.isRowSelectable) obj.isRowSelectable = props.isRowSelectable;
      if (props?.getRowClassName) obj.getRowClassName = props.getRowClassName;
      if (mode === "server") {
        if (obj?.slotProps?.toolbar)
          obj.slotProps.toolbar = {
            ...obj.slotProps?.toolbar,
            searchQuery: searchQuery,
            onSearchChange: handleSearch,
          };
        obj.onRowSelectionModelChange = rowSelectionHandler;
        obj.rowSelectionModel = rowSelectionModel;
        obj.keepNonExistentRowsSelected = true;
        obj.rowCount = rowCount;
        obj.paginationMode = "server";
        obj.sortingMode = "server";
        obj.filterMode = "server";
        obj.paginationModel = paginationModel;
        obj.onPaginationModelChange = setPaginationModel;
        obj.rowCount = rowCount;
        obj.onSortModelChange = handleSortChange;
      } else {
      }
      return {
        ...obj,
        loading,
        pageSizeOptions: [5, 10, 20, 50, 100, { value: -1, label: "All" }],
        localeText: {
          footerRowSelected: (count) => {
            if (mode === "server") {
              return isSelectAll
                ? "All Valid Row Selected"
                : `${count} Row Selected`;
            } else {
              return count === rowCount
                ? "All Row Selected"
                : `${count} Row Selected`;
            }
          },
        },
      };
    },
    mode === "server"
      ? [data, filterModel, rowSelectionModel, rowCount, columnVisibilityModel]
      : [data, filterModel, columnVisibilityModel]
  );
  return (
    <div style={{ width: "100%" }}>
      <DataGrid
        className="datagrid_custom"
        {...tableProps}
        {...{ rows: data, loading: loading }}
        filterModel={filterModel}
        sx={{
          display: "flex",
          flexDirection: "column",
        }}
      />
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          padding: "10px",
          fontWeight: "bold",
        }}
      >
        Total: ₹ {filterdTotal}
      </div>
      <style>
        {`
    @media print{
        .MuiDataGrid-cell,.MuiDataGrid-columnHeader {
      width: calc(
        85% / ${
          columns.length -
          Object.values(columnVisibilityModel).filter((val) => !val).length
        }
      ) !important;
      min-width: 50px !important;
    }
    }
    `}
      </style>
    </div>
  );
};

export default DI(SimpleTable);
